import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, ChevronRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [newsletter, setNewsletter] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password || (mode === "register" && !form.name)) {
      setError("Veuillez remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password, newsletter);
      navigate(redirect ? `/${redirect}` : "/");
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-5 py-12">
      <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-8">
        <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
        <ChevronRight size={15} />
        <span className="text-slate-50 font-semibold">{mode === "login" ? "Connexion" : "Inscription"}</span>
      </div>

      <div className="max-w-md mx-auto bg-[#10131A] rounded-2xl border border-[#1E232D] p-6 sm:p-8 shadow-sm">
        <div className="flex rounded-lg bg-[#181C26] p-1 mb-7">
          <button onClick={() => { setMode("login"); setError(""); }} className={`flex-1 py-2.5 rounded-md font-bold text-[15px] transition-colors ${mode === "login" ? "bg-[#10131A] text-slate-50 shadow-sm" : "text-slate-400"}`}>Se connecter</button>
          <button onClick={() => { setMode("register"); setError(""); }} className={`flex-1 py-2.5 rounded-md font-bold text-[15px] transition-colors ${mode === "register" ? "bg-[#10131A] text-slate-50 shadow-sm" : "text-slate-400"}`}>Créer un compte</button>
        </div>

        <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-1">{mode === "login" ? "Bon retour !" : "Bienvenue !"}</h1>
        <p className="text-slate-400 text-[14px] mb-6">{mode === "login" ? "Connectez-vous à votre compte KTSauto." : "Créez votre compte en quelques secondes."}</p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Nom complet</label>
              <div className="flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F] transition-colors">
                <User size={18} className="text-slate-500" />
                <input name="name" value={form.name} onChange={onChange} placeholder="Jean Dupont" className="flex-1 px-3 py-3 outline-none text-slate-50" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Adresse email</label>
            <div className="flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F] transition-colors">
              <Mail size={18} className="text-slate-500" />
              <input data-testid="auth-email" name="email" type="email" value={form.email} onChange={onChange} placeholder="vous@email.fr" className="flex-1 px-3 py-3 outline-none text-slate-50" />
            </div>
          </div>
          <div>
            <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Mot de passe</label>
            <div className="flex items-center border-2 border-[#222735] rounded-lg px-3 focus-within:border-[#E01F1F] transition-colors">
              <Lock size={18} className="text-slate-500" />
              <input data-testid="auth-password" name="password" type={showPw ? "text" : "password"} value={form.password} onChange={onChange} placeholder="••••••••" className="flex-1 px-3 py-3 outline-none text-slate-50" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="text-slate-500 hover:text-white">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
          </div>
          {mode === "register" && (
            <label className="flex items-start gap-2.5 text-[13px] text-slate-300 cursor-pointer" data-testid="auth-newsletter-label">
              <input data-testid="auth-newsletter" type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#E01F1F]" />
              <span>Je souhaite recevoir la newsletter KTSauto (promos, nouveautés, conseils d'entretien — 1 email par mois max, désinscription en un clic).</span>
            </label>
          )}
          {error && <div className="text-[14px] text-red-500">{error}</div>}
          {mode === "login" && (<div className="text-right"><Link to="/mot-de-passe-oublie" data-testid="forgot-password-link" className="text-[13px] text-[#FF3B3B] hover:underline">Mot de passe oublié ?</Link></div>)}
          <button data-testid="auth-submit" type="submit" disabled={loading} className="btn-orange w-full rounded-lg py-3.5 font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-70">
            {loading && <Loader2 className="animate-spin" size={18} />} {mode === "login" ? "Se connecter" : "Créer mon compte"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
